/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-17
 */

// Add third-party dependencies.
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Stack,
} from '@mui/material';

// Add local dependencies.
import {
  useCreateMasterItemMutation,
  useUpdateMasterItemMutation,
} from '../../../api/classEssentialsApi';
import type { MasterItem } from '../../../types/classEssentials';

/**
 * Props for MasterItemDialog component
 */
interface MasterItemDialogProps {
  open: boolean;
  onClose: () => void;
  studioId: string;
  item: MasterItem | null;
}

/**
 * Master Item Dialog component.
 * Form dialog for creating and editing master items.
 *
 * @param props - Component props
 * @returns {React.ReactElement} The master item dialog component
 */
const MasterItemDialog: React.FC<MasterItemDialogProps> = ({
  open,
  onClose,
  studioId,
  item,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createItem, { isLoading: isCreating }] = useCreateMasterItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateMasterItemMutation();

  const isLoading = isCreating || isUpdating;

  // Reset form when dialog opens/closes or item changes
  useEffect(() => {
    if (open) {
      if (item) {
        setName(item.name);
        setDescription(item.description);
        setCategory(item.category);
        setPrice(item.price ? String(item.price) : '');
        setIsAvailable(item.is_available);
      } else {
        setName('');
        setDescription('');
        setCategory('');
        setPrice('');
        setIsAvailable(true);
      }
      setError(null);
    }
  }, [open, item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!category.trim()) {
      setError('Category is required');
      return;
    }

    try {
      const itemData = {
        studio: studioId,
        name: name.trim(),
        description: description.trim(),
        category: category.trim(),
        price: price ? parseFloat(price) : null,
        is_available: isAvailable,
      };

      if (item) {
        await updateItem({ id: item.id, data: itemData }).unwrap();
      } else {
        await createItem(itemData).unwrap();
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to save item:', err);
      setError(err?.data?.detail || 'Failed to save item. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{item ? 'Edit Item' : 'Add New Item'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              label="Item Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              placeholder="e.g., Ballet Shoes"
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              required
              placeholder="e.g., Footwear, Apparel, Accessories"
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              multiline
              rows={3}
              placeholder="Detailed description of the item..."
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Price"
              value={price}
              onChange={e => setPrice(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              placeholder="0.00"
              disabled={isLoading}
              helperText="Optional - leave blank if price varies"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isAvailable}
                  onChange={e => setIsAvailable(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Available for selection"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {item ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MasterItemDialog;
